import {
	FormProvider,
	getFormProps,
	getInputProps,
	useInputControl,
} from '@conform-to/react'
import {
	Badge,
	Box,
	Flex,
	Heading,
	Popover,
	Separator,
	Text,
	Tooltip,
	VisuallyHidden,
} from '@radix-ui/themes'
import { config as springConfig } from '@react-spring/core'
import { useTransition as useTransitionSpring } from '@react-spring/web'
import { Link, useLocation, useSearchParams } from '@remix-run/react'
import cn from 'clsx'
import parse from 'html-react-parser'
import * as React from 'react'
import { z } from 'zod'
import { FormDefaults } from '#app/components/common/form-defaults.tsx'
import { Search as SearchIcon, ShoppingBag_2 } from '#app/components/icon'
import { Image } from '#app/components/ui/image.tsx'
import { type GetProducts } from '#app/models/ecommerce-providers/sylius/shop/fetch/product/get-products.ts'
import { type Locale } from '#app/models/locale.ts'
import { type App } from '#app/routes/_layout/component.route.tsx'
import {
	ROOT_APP_VIEW,
	type FormEnum,
} from '#app/routes/_layout/route-config.ts'
import { SEARCH_SEARCH_PARAMS } from '#app/routes/_layout/search'
import { testId } from '#app/utils/client/test-id.ts'
import { schemaDefault } from '#app/utils/form-defaults.ts'
import { useSearchForm } from '#app/utils/hooks/forms/use-search-form.tsx'

/* -------------------------------------------------------------------------------------------------
 * Search form
 * -----------------------------------------------------------------------------------------------*/

enum TEST_IDS {
	search = 'search',
	cartLink = 'cart-link',
}

enum INPUTS {
	q = 'q',
}

const FORM: FormEnum = {
	id: 'search-form',
	action: '/actions/search',
	fetcherKey: 'search',
	subaction: 'search',
	redirect: '/products',
} as const

const schema = z.object({
	...schemaDefault,
})

type SearchFormProps = {
	cartCount: number
	locale: Locale
	setApp: React.Dispatch<React.SetStateAction<App>>
}

const SearchForm: React.FC<SearchFormProps> = ({
	cartCount,
	locale,
	setApp,
}) => {
	const {
		searchFetcherKey,
		searchFetcher,
		searchFormValues,
		searchForm,
		searchFields,
		t,
	} = useSearchForm()

	const [_, setSearchParams] = useSearchParams()

	const query = useInputControl(searchFields[INPUTS.q])

	const [open, setOpen] = React.useState(false)
	const inputRef = React.useRef<HTMLInputElement | null>(null)
	const location = useLocation()
	const [container, setContainer] = React.useState<HTMLElement | null>(null)
	const transitions = useTransitionSpring(open, {
		from: { opacity: 0, y: -10 },
		enter: { opacity: 1, y: 0 },
		leave: { opacity: 0, y: 10 },
		config: springConfig.slow,
	})

	const [isSearch, setIsSearch] = React.useState(false)

	return (
		<FormProvider context={searchForm.context}>
			<Flex
				asChild
				align="center"
				position="relative"
				p="2"
				height="3rem"
				minWidth={{ xl: '20rem' }}
				{...testId(TEST_IDS.search)}
			>
				<searchFetcher.Form
					method="get"
					action={FORM.action}
					{...getFormProps(searchForm)}
					className={cn('rounded-full border bg-accent-7 text-gray-2', {
						'border-accent-9': isSearch,
						'border-transparent': !isSearch,
					})}
				>
					<FormDefaults
						form={searchForm}
						fields={searchFields}
						values={searchFormValues}
					/>
					<Popover.Root open={open} onOpenChange={setOpen}>
						<Popover.Trigger>
							<Flex justify="between" align="center" width="100%">
								<Flex align="center">
									<SearchIcon
										className="mr-2 fill-accent-9"
										width={27}
										height={27}
									/>
									<input
										{...getInputProps(searchFields[INPUTS.q], {
											type: 'text',
											value: query,
										})}
										ref={inputRef}
										className="search"
										placeholder={t('label.search')}
										onFocus={(e) => {
											if (!isSearch) {
												searchFetcher.submit(e.currentTarget.form, {
													fetcherKey: searchFetcherKey,
													debounceTimeout: 0,
												})
											}
											setIsSearch(true)
										}}
										onBlur={() => setIsSearch(false)}
										onChange={(e) => {
											e.preventDefault()
											if (e.currentTarget.value.length) {
												searchFetcher.submit(e.currentTarget.form, {
													fetcherKey: searchFetcherKey,
													debounceTimeout: 200,
												})
												setOpen(true)
											}
											query.change(
												e.currentTarget.value.replace(/[^a-zA-Z ]/g, ''),
											)
											setSearchParams((prev) => {
												if (e.currentTarget.value !== '') {
													prev.set(
														SEARCH_SEARCH_PARAMS.q,
														e.currentTarget.value,
													)
												} else {
													prev.delete(SEARCH_SEARCH_PARAMS.q)
												}
												return prev
											})
										}}
										autoComplete="off"
										autoCorrect="off"
										autoCapitalize="none"
										spellCheck="false"
									/>
								</Flex>
								<Flex align="center">
									<Separator orientation="vertical" color="green" />
									<Tooltip
										content={t('label.cart', { count: cartCount })}
										sideOffset={9}
									>
										<Box position="relative" display="inline-block" ml="3">
											<Link
												{...testId(TEST_IDS.cartLink)}
												to={`/${locale}/cart`}
												className="group hover:text-gray-7"
												onClick={(event) => {
													if (location.pathname === `/${locale}/cart`) {
														return
													}

													event.preventDefault()

													setApp((prev) => ({
														...prev,
														sidebarView: ROOT_APP_VIEW.cartView,
														displaySidebar: true,
													}))
												}}
											>
												<VisuallyHidden>
													{t('label.cart', { count: cartCount })}
												</VisuallyHidden>

												<ShoppingBag_2
													className="mr-1 fill-accent-9"
													width={30}
													height={30}
												/>

												{!!cartCount && (
													<Box
														asChild
														data-testid="cart-count"
														position="absolute"
														top="-10px"
														right="-10px"
													>
														<Badge
															size="1"
															color="green"
															radius="full"
															className="bg-green-4"
														>
															{cartCount}
														</Badge>
													</Box>
												)}
											</Link>
										</Box>
									</Tooltip>
								</Flex>
							</Flex>
						</Popover.Trigger>
						<Popover.Content
							container={container}
							onOpenAutoFocus={(e) => {
								e.preventDefault()
								inputRef.current?.focus()
							}}
							side="bottom"
						>
							{transitions((styles, item) =>
								item ? (
									// @ts-ignore
									inputRef.current?.value.length &&
									searchFetcher.data?.products ? (
										// @ts-ignore
										searchFetcher.data.products.items?.length ? (
											<Flex asChild direction="column" mt="1" gapY="3">
												<ul>
													{
														// @ts-ignore
														(
															searchFetcher.data.products as GetProducts
														).items!.map(({ name, slug, images }) => (
															<li key={slug}>
																<Flex asChild align="center" gapX="3" p="1">
																	<Link
																		prefetch="viewport"
																		to={`/${locale}/products/${slug}`}
																		className="rounded transition duration-300 ease-in-out hover:shadow"
																		onClick={() => setOpen(false)}
																	>
																		<Box width="3rem">
																			<Image
																				alt={name!}
																				src={images![0].path!}
																			/>
																		</Box>
																		<Heading
																			as="h4"
																			size="2"
																			weight="medium"
																			color="gray"
																			className="!break-keep"
																		>
																			{query.value === '' ? (
																				<>{name}</>
																			) : (
																				<>
																					{parse(
																						name!.replace(
																							new RegExp(
																								query.value as string,
																								'gi',
																							),
																							(match) =>
																								`<mark style="background-color: hsl(122, 40%, 85%); border-radius: 0.375rem; padding: 0.125rem;">${match}</mark>`,
																						),
																					)}
																				</>
																			)}
																		</Heading>
																	</Link>
																</Flex>
															</li>
														))
													}
												</ul>
											</Flex>
										) : (
											<Text
												align="center"
												mt="2"
												color="gray"
												weight="medium"
												size="2"
											>
												{t('common.noItemsFound')}
											</Text>
										)
									) : (
										<Text
											align="center"
											mt="2"
											color="gray"
											weight="medium"
											size="2"
										>
											{t('common.typeToStartSearch')}
										</Text>
									)
								) : <></>,
							)}
						</Popover.Content>
					</Popover.Root>
					<Box ref={setContainer} />
				</searchFetcher.Form>
			</Flex>
		</FormProvider>
	)
}
SearchForm.displayName = 'SearchForm'

/* -----------------------------------------------------------------------------------------------*/

export {
	type SearchFormProps,
	SearchForm,
	TEST_IDS as SEARCH_TEST_IDS,
	INPUTS as SEARCH_INPUTS,
	FORM as SEARCH_FORM,
	schema as searchSchema,
}
